import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import type { MobileNavItem } from '@ld.consumer-portal/models';
import { filter, map, startWith } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MobileNavService {
  private readonly router = inject(Router);

  readonly activeTab = signal<string>('home');

  private readonly routeActiveTab = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.syncActiveTabFromUrl(event.urlAfterRedirects)),
      filter((tab): tab is string => tab !== null),
    ),
  );

  private syncActiveTabFromUrl(url: string): string | null {
    if (url.startsWith('/calculators')) {
      return 'calculators';
    }
    if (url.startsWith('/dashboard')) {
      const current = this.activeTab();
      if (current !== 'home' && current !== 'products') {
        return 'home';
      }
    }
    return null;
  }

  private readonly syncEffect = effect(() => {
    const tab = this.routeActiveTab();
    if (tab) {
      this.activeTab.set(tab);
    }
  });

  private readonly isCpScreen = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map((event) => this.shouldHideBottomNav(event?.urlAfterRedirects ?? this.router.url)),
    ),
    {
      initialValue: this.shouldHideBottomNav(this.router.url),
    },
  );

  readonly isBottomNavVisible = computed(() => !this.isCpScreen());

  private shouldHideBottomNav(url: string): boolean {
    if (!url) return false;

    const isCpUrl =
      url.includes('/origination/') ||
      url.includes('/apply/refinance') ||
      url.includes('/apply/purchase') ||
      url.includes('hideBottomNav=true') ||
      url.includes('/loans/') ||
      url.includes('/help/') ||
      url.includes('/messages/') ||
      url.includes('/manage-paperless/') ||
      url.includes('/contact-information') ||
      url.includes('/dashboard/link-loan') ||
      url.includes('/verify-employments') ||
      url.includes('/verify-assets') ||
      url.includes('/template-builder');

    return isCpUrl;
  }

  setActiveTab(tabId: string): void {
    this.activeTab.set(tabId);
  }

  onNavItemClick(navItem: MobileNavItem): void {
    this.activeTab.set(navItem.id);

    switch (navItem.id) {
      case 'home':
        this.router.navigate(['/dashboard']);
        break;
      case 'products':
        this.router.navigate(['/dashboard']);
        break;
      case 'calculators':
        this.router.navigate(['/calculators']);
        break;
      default:
        if (navItem.route) {
          this.router.navigate([navItem.route]);
        }
    }
  }

  reset(): void {
    this.activeTab.set('home');
  }
}
